// Единицы тарификации — то, в чём пользователь считает подписку.
//
// Источник: design/README.md, ряд остатков на экране 02 и экран 10.
// Продуктовое требование: единица описывает результат («1 вопрос»),
// а не ресурс («1000 токенов»). Пользователь не должен знать про токены.
//
// 06.09.2026 единиц стало меньше на две. Вопросы, поиски и проверки слиты в
// одну — «вопросы», — и это не упрощение кода, а исправление продукта:
// человек, задающий вопрос, не различает «веб-поиск» и «подтверждение по
// источникам». Для него это один жест — спросить, — а чем его выполнить,
// решаем мы. Три счётчика на один жест заставляли выбирать между словами,
// значения которых он не знает, и обнулялись они по отдельности: кончились
// проверки — «спросить» перестало работать наполовину, без объяснения.
//
// В тот же день проверка фактов ушла из продукта совсем: слияние сняло с неё
// цену, но не сложность — карточка с утверждениями и статусами «подтверждено /
// спорно / не найдено» осталась непонятной. А следом за ней, на том же круге,
// ушёл и сам поиск как отдельный сценарий: его карточка отвечала пересказом
// выдачи, а бесплатная правка переписывала свод, не ища заново (scenarios.ts).
//
// Единицу это не изменило и не могло: слияние уже свело всё это к «вопросу».
// Изменилось только то, чем вопрос отвечается — веб-поиск стал этапом внутри
// обычного ответа, а не отдельной работой со своей ценой.
//
// Что при этом НЕ слито: документы и изображения. Их человек как раз
// различает — он приносит файл или просит картинку, — и стоят они другого
// порядка. Единица должна совпадать с намерением, а не со стоимостью.

/** Все единицы, включая те, что в MVP не входят. */
export const UNIT_IDS = ['questions', 'research', 'documents', 'images'] as const

export type UnitId = (typeof UNIT_IDS)[number]

/**
 * Глубокое исследование нарисовано в дизайне, но вынесено за скоуп MVP
 * (P4: «Видео и глубокое исследование — вне MVP»). Тип оставлен, чтобы
 * баланс с сервера не ломал клиент, когда единицу включат.
 */
export const MVP_UNIT_IDS = ['questions', 'documents', 'images'] as const satisfies readonly UnitId[]

export type MvpUnitId = (typeof MVP_UNIT_IDS)[number]

export const isMvpUnit = (id: UnitId): id is MvpUnitId =>
  (MVP_UNIT_IDS as readonly UnitId[]).includes(id)

/** Родительный падеж нужен для «осталось 5 вопросов» — склонение на клиенте не собрать. */
export interface UnitLabel {
  /** Именительный, единственное: «вопрос». */
  one: string
  /** Родительный, единственное — для 2–4: «вопроса». */
  few: string
  /** Родительный, множественное — для 0 и 5+: «вопросов». */
  many: string
}

export const UNIT_LABELS: Record<UnitId, UnitLabel> = {
  questions: { one: 'вопрос', few: 'вопроса', many: 'вопросов' },
  research: { one: 'исследование', few: 'исследования', many: 'исследований' },
  documents: { one: 'документ', few: 'документа', many: 'документов' },
  images: { one: 'изображение', few: 'изображения', many: 'изображений' },
}

/**
 * Множественное без числительного: «больше не спрашивать про документы».
 *
 * Отдельной таблицей, а не четвёртым полем в UnitLabel. UnitLabel — про счёт
 * («3 источника»), и этой формы там не нужно ни плашке этапа, ни строке
 * остатка, ни одному из локальных ярлыков в сценариях. Нужна она ровно
 * подписи к чекбоксу на экране 04, и это единицы тарификации, а не что угодно
 * считаемое.
 *
 * Заводить четвёртую форму ради одной фразы уже отказывались (SMY-46:
 * «запустить за 1 проверку» переписали в именительный). Здесь обойти
 * винительный падеж нечем — это готовый копирайт дизайна.
 */
export const UNIT_PLURALS: Record<UnitId, string> = {
  questions: 'вопросы',
  research: 'исследования',
  documents: 'документы',
  images: 'изображения',
}

/** Русское склонение после числительного. «1 вопрос», «2 вопроса», «5 вопросов». */
export function pluralize(count: number, label: UnitLabel): string {
  const n = Math.abs(count) % 100
  const tens = n % 10
  if (n > 10 && n < 20) return label.many
  if (tens > 1 && tens < 5) return label.few
  if (tens === 1) return label.one
  return label.many
}

export const formatUnit = (count: number, id: UnitId): string =>
  `${count} ${pluralize(count, UNIT_LABELS[id])}`

export interface Balance {
  unit: UnitId
  /** Сколько осталось в текущем периоде. */
  remaining: number
  /** Лимит тарифа на период. */
  limit: number
  /**
   * Пора предупреждать: на экране 10 полоса такой категории красится в
   * `status-warn`, а не в `accent`. Считает сервер — порог живёт в
   * конфигурации, и клиент не должен знать его вторым числом.
   */
  low: boolean
  /**
   * Сколько в остатке пришло из живых ПАКЕТОВ — купленных и начисленных
   * поддержкой, — а не из лимита тарифа. Источника в числе нет, и клиент не
   * вправе назвать пакеты купленными (SMY-325, сторож
   * `scripts/check-packs-wording.mjs`).
   *
   * Поля не было, хотя сервер его слал: `remaining` включает пакеты, `limit`
   * нет, и экран показывал «50 из 30» с полосой за краем. Купивший пакет
   * видел число, которое не сходится ни с чем.
   *
   * Отсюда правило показа: месяц и пакеты — РАЗНЫЕ строки. Они и живут
   * по-разному: лимит тарифа обнуляется каждый период, пакет не сгорает
   * вовсе (`packs.expiresAt` необязателен) и тратится после месячного.
   * Сложить их в одно число значит пообещать, что всё это сгорит первого
   * числа.
   *
   * Отсутствует, когда пакетов нет: ноль здесь означал бы «пакет куплен и
   * израсходован», а это другое состояние.
   */
  fromPacks?: number
}

/**
 * Близка ли категория к исчерпанию. SMY-59
 *
 * Доли одной мало: 20% от 15 документов — это три штуки, а 20% от трёх
 * документов — 0,6, то есть предупреждение появилось бы только на нуле, когда
 * предупреждать уже поздно. Отсюда пол в одну единицу: последняя оставшаяся
 * всегда считается тревожной.
 *
 * На числах дизайна экрана 10 это даёт ровно нарисованное: «Документы 2 из 15»
 * тревожны, «Вопросы 9 из 10» и «Изображения 20 из 20» — нет.
 */
export function isLowBalance(remaining: number, limit: number, share: number): boolean {
  if (limit <= 0) return false
  return remaining <= Math.max(1, Math.ceil(limit * share))
}

// --- Спецификация сценария. SMY-57 ------------------------------------------

/**
 * Что именно считает `size` у результата.
 *
 * Нужно не для логики, а для формулировки: экран 04 обязан назвать состав
 * результата словами человека — «до 7 источников», «до 5 утверждений», — и
 * число само по себе на это не отвечает.
 */
export const SIZE_KINDS = ['answers', 'sources', 'pages', 'variants'] as const
export type SizeKind = (typeof SIZE_KINDS)[number]

/**
 * Объём одного результата.
 *
 * Единственное число на сценарий — и это решение, а не упрощение. Спецификация
 * из P5 у каждого сценария упирается ровно в одну величину: у чата ответ, у
 * документа страницы, у изображения варианты. Набор необязательных полей на
 * все случаи дал бы четыре пустых места из пяти и вопрос, что значит «страниц
 * не задано» у изображения.
 *
 * Число источников веб-поиска сюда поэтому и НЕ попало (06.09.2026): это не
 * объём результата, а то, чем результат собран, и у чата теперь есть и то и
 * другое. Оно живёт отдельной строкой конфигурации — `web.sources`.
 *
 * Ключом СЦЕНАРИЙ, а не единица: чат и «Написать» списываются одной единицей
 * «вопрос», а объём у них свой. Объём — свойство работы, а единица — свойство
 * кошелька, и держать их на одном ключе значило бы требовать, чтобы всё, что
 * стоит одинаково, и делалось одинаково.
 */
export interface UnitScope {
  /** Сколько влезает в один результат: источников, страниц, вариантов. */
  size: number
  kind: SizeKind
}

/** Склонения того, что считает `size`. «7 источников», «10 страниц». */
export const SIZE_LABELS: Record<SizeKind, UnitLabel> = {
  answers: { one: 'ответ', few: 'ответа', many: 'ответов' },
  sources: { one: 'источник', few: 'источника', many: 'источников' },
  pages: { one: 'страница', few: 'страницы', many: 'страниц' },
  variants: { one: 'вариант', few: 'варианта', many: 'вариантов' },
}

export const formatSize = (scope: UnitScope): string =>
  `${scope.size} ${pluralize(scope.size, SIZE_LABELS[scope.kind])}`

/**
 * Сколько уточнений в чате идёт без списания после оплаченного результата.
 *
 * Не любезность, а условие того, что единица стала одна. Пока лимитов было
 * три, дешёвый разговор оплачивался дешёвой единицей: переспросить стоило
 * «вопрос» за 0,09 ₽. В общем котле переспрос стоил бы столько же, сколько
 * поиск, — и человек, задавший уточняющий вопрос, потратил бы на него единицу
 * той же цены, что и на сам результат.
 *
 * Отсюда правило: уточнения в том же чате идут бесплатно и ВСЕГДА по самому
 * дешёвому маршруту — обычным ответом, без веб-поиска. Это ровно то, чем
 * уточнение и является: «а что это значит», а не новая задача.
 *
 * С 06.09.2026 это ещё и единственный способ переспросить: бесплатной правки
 * у ответа больше нет, потому что нет и карточки, которую она правила. И это
 * к лучшему — правка переписывала прежний текст, а уточнение отвечает заново
 * (scenarios.ts).
 */
export const FREE_FOLLOWUPS = 3

/**
 * Склонение слова «уточнение».
 *
 * Живёт здесь, а не в клиенте, по той же причине, по которой числа не лежат
 * в вёрстке: клиентов два, и вторая копия расходится с первой молча. До
 * 08.09.2026 этот объект был объявлен ДВАЖДЫ — в `Chat.tsx` веба и в
 * `Chat.tsx` телефона, слово в слово (SMY-148).
 */
export const CLARIFICATION: UnitLabel = {
  one: 'уточнение',
  few: 'уточнения',
  many: 'уточнений',
}

/**
 * Обещание про бесплатные уточнения — там, где объясняем лимиты.
 *
 * Число ПОДСТАВЛЯЕТСЯ, а не пишется словом, и это тот же довод, что у
 * ScenarioSpec ниже: строки собираются из чисел. До 08.09.2026 фраза была
 * записана прозой в обоих клиентах — «И ещё три уточнения…», — и смена
 * FREE_FOLLOWUPS оставила бы оба экрана уверенно говорить «три» (SMY-148).
 *
 * Сказуемое безличное («бесплатно», не «бесплатны») намеренно: при
 * FREE_FOLLOWUPS = 1 согласование с «уточнение» иначе разъезжается, а
 * фраза, верная только при сегодняшнем числе, — та же беда, от которой
 * подстановка и заводилась.
 */
export function freeFollowupsPromise(n: number = FREE_FOLLOWUPS): string {
  return `И ещё ${n} ${pluralize(n, CLARIFICATION)} к каждому ответу — бесплатно: переспрашивать можно спокойно.`
}

/**
 * Плашка над полем ввода: бесплатные уточнения к прошлому ответу вышли.
 *
 * Числа здесь НЕТ намеренно. «Три бесплатных уточнения… закончились» несёт
 * сразу два согласования — прилагательное и глагол, — и при единице ломаются
 * оба. Плашка отвечает на вопрос «почему этот вопрос спишется», а не «сколько
 * их было»: сколько — сказано в объяснении лимитов, где число подставляется.
 *
 * «Уточнения и правки» и «сообщение», а не «вопрос», — с 21.09.2026 (SMY-336):
 * правки и уточнения делят один запас (п. 6.4 правил), и кончиться он мог на
 * правках, а следующим человек мог написать и правку.
 */
export const FREE_FOLLOWUPS_SPENT =
  'Бесплатные уточнения и правки прошлого ответа закончились — это сообщение спишется с лимита.'

/**
 * Состав результата и момент списания — то, что показывает экран 04.
 *
 * Строки собираются из чисел, а не лежат готовыми. Причина прямая: числа
 * меняются из админки после беты (SMY-89), и застывший текст «До 5
 * утверждений» пережил бы смену лимита на 3, не изменившись. Обещание,
 * разошедшееся с поведением, хуже отсутствующего.
 */
export interface ScenarioSpec {
  unit: UnitId
  /** Строки с галочками: что входит в единицу. */
  includes: string[]
  /** Когда именно спишется. */
  chargedWhen: string
}

/**
 * Что не списывается — общее для всех единиц.
 *
 * Правила доверия из P5, слово в слово. Живут в ядре, а не в вёрстке: их
 * показывает и экран 04 сноской, и экран 11 разделом «Правила списания
 * единиц», и расходиться этим двум местам нельзя.
 */
export const NOT_CHARGED: readonly string[] = [
  'Уточняющие вопросы — считается только готовый результат',
  `Ещё ${FREE_FOLLOWUPS} ${pluralize(FREE_FOLLOWUPS, CLARIFICATION)} в том же чате после ответа`,
  'Технические ошибки и сбои на нашей стороне',
  'Отмена до старта и во время выполнения',
  'Одна правка результата в окне 10 минут',
]

/**
 * Бесплатных возвратов непонятой правки на один результат. SMY-319
 *
 * Непонятая правка возвращается (`releaseEdit`), и без предела круг «правка →
 * возврат → правка» давал сколько угодно бесплатных вызовов модели со всем
 * результатом на входе — держала только частота запусков, до шестидесяти в час.
 * Решение владельца 17.09.2026: три возврата на результат, дальше непонятая
 * правка засчитывается, как обычная.
 *
 * ⚠️ ЧИСЛО ОПУБЛИКОВАНО — п. 6.3 правил списания единиц, редакция от 17.09.2026.
 * Поменял — выпускай редакцию (`docs/legal/units.md`); сверяет их проверка в
 * `routes/artifacts.test.ts`. Действует на все результаты сразу: по п. 9.2 правил
 * новая редакция вступает в силу через десять дней, но реальных пользователей
 * на 17.09.2026 нет, и владелец решил включить предел без отсрочки.
 */
export const FREE_EDIT_RETURNS = 3

/**
 * Сколько правок результата входит в списание и как долго они бесплатны.
 *
 * Правило было одно на всё: одна правка в окне десяти минут. Оказалось, что
 * это два разных случая, и мерить их надо разным.
 *
 * У ИЗОБРАЖЕНИЯ правка — это новая генерация: поставщик редактировать
 * присланную картинку не умеет (docs/models.md), и «поправь фон» стоит нам
 * ровно столько же, сколько сама картинка. Значит, считать надо ВРЕМЯ — и
 * коротко: три минуты это «я посмотрел и сразу передумал», а не «вернулся
 * через полчаса и захотел ещё одну».
 *
 * У ДОКУМЕНТА наоборот. Правка разбора стоит нам разбора же, но человек
 * читает договор не три минуты, и окно по времени наказывало бы за
 * внимательность. Считать надо ЧИСЛО: три уточнения к одному документу
 * укладываются в обещание «разбор», сколько бы времени он ни занял.
 */
export interface EditPolicy {
  /** Сколько правок входит в списание. */
  count: number
  /** Сколько минут после выдачи. Не задано — окна нет, считается только число. */
  minutes?: number
}
